import { api, useApiQuery } from "../../api";
import type { Charge, Uuid } from "../../api";
import { Badge, Card, EmptyState, Table } from "../../components";
import type { BadgeTone, TableColumn } from "../../components";
import { formatDateTime, formatEuros, formatTokens } from "../../format";
import { AsyncBoundary } from "../../shell";

/**
 * Ce qu'un prélèvement dit à qui le lit. Un rejet après crédit est un
 * impayé — l'entreprise a reçu ses tokens et doit encore les payer —, un
 * refus d'emblée n'a rien coûté : les deux ne se ressemblent pas à l'écran.
 */
function statut(c: Charge): { libelle: string; ton: BadgeTone } {
  switch (c.status) {
    case "pending":
      return { libelle: "En attente", ton: "neutral" };
    case "processing":
      return { libelle: "Prélèvement en cours", ton: "accent" };
    case "paid":
      return { libelle: "Payé", ton: "success" };
    case "failed":
      return c.credited ? { libelle: "Impayé", ton: "danger" } : { libelle: "Refusé", ton: "warning" };
  }
}

const COLONNES: TableColumn<Charge>[] = [
  { key: "date", header: "Date", render: (c) => formatDateTime(c.created_at) },
  { key: "nature", header: "Nature", render: (c) => (c.kind === "topup" ? "Recharge automatique" : "Achat de pack") },
  { key: "montant", header: "Montant", numeric: true, render: (c) => formatEuros(c.amount_cents) },
  { key: "tokens", header: "Tokens", numeric: true, render: (c) => formatTokens(c.tokens) },
  {
    key: "statut",
    header: "Statut",
    render: (c) => {
      const s = statut(c);
      return (
        <span>
          <Badge tone={s.ton} size="sm">
            {s.libelle}
          </Badge>
          {c.failure_reason !== null && <> {c.failure_reason}</>}
        </span>
      );
    },
  },
  {
    key: "facture",
    header: "Facture",
    render: (c) =>
      c.invoice_url !== null ? (
        <a href={c.invoice_url} target="_blank" rel="noreferrer">
          Voir la facture
        </a>
      ) : (
        "—"
      ),
  },
];

/** Les derniers prélèvements d'une entreprise, pour elle et pour l'ESN. */
export function Prelevements({ companyId }: { companyId: Uuid }) {
  const liste = useApiQuery((o) => api.listCompanyCharges(companyId, o), [companyId]);
  return (
    <Card
      title="Prélèvements"
      subtitle="Recharges automatiques et packs achetés, avec leur facture. Les tokens sont crédités dès que le prélèvement est lancé."
      padding="none"
    >
      <AsyncBoundary state={liste} errorTitle="Prélèvements illisibles">
        {(r) => (
          <Table
            caption={<span className="lde-visually-hidden">Prélèvements de l'entreprise</span>}
            columns={COLONNES}
            rows={r.charges}
            rowKey={(c) => c.id}
            animateRows={false}
            empty={<EmptyState compact title="Aucun prélèvement." description="Rien n'a encore été prélevé sur le moyen de paiement." />}
          />
        )}
      </AsyncBoundary>
    </Card>
  );
}
